import React, { PropsWithChildren } from 'react';
import { AbstractControl, FieldControl, FieldGroup, FormBuilder, FormGroup } from 'react-reactive-form';
import { MultiColumnsSelectItemModel } from './TwoColumnsSelect';
import Checkbox from './Checkbox';
import { formatItemToString } from '../../helpers/format.helper';
import { WithTranslation, withTranslation } from 'react-i18next';

interface TwoColumnsSelectProps extends AbstractControl, WithTranslation {
  selectedAttributeName?: string;
  sortAttributeName?: string;
  itemValues?: MultiColumnsSelectItemModel[];
  defaultSelectedIds?: string[];
  filterAttributes?: string[];
  handleValueChange?: (selectedItems: MultiColumnsSelectItemModel[]) => void;
  primaryAtr?: string;
  secondaryAtr?: string;
  disabledValues?: string[];
  showInModal: boolean;
}

interface TwoColumnsSelectState {
  selectedCtrls: FormGroup[];
  allCtrls: FormGroup[];
  filter: any;
  codeAtrWidth: string;
  selectAllOptionsElement?: FormGroup;
}

export const MultiSelect = withTranslation()(
  class MultiSelectElement extends React.Component<PropsWithChildren<TwoColumnsSelectProps>, TwoColumnsSelectState> {
  private searchElRef: React.RefObject<HTMLInputElement>;

  private allElementsListRef: HTMLDivElement | undefined;

  private codeAtrRefs: any;

  constructor(props: any) {
    super(props);

    this.state = {
      selectedCtrls: [],
      allCtrls: [],
      filter: undefined,
      codeAtrWidth: 'auto'
    };
    this.searchElRef = React.createRef();
    this.codeAtrRefs = React.createRef();
    this.codeAtrRefs.current = [];
  }

  componentDidMount() {
    if (this.props.value?.items && !this.props.itemValues) {
      this.generateControls(this.props.value?.items, this.props.value?.selected.map((itm: any) => formatItemToString(itm)));
    } else if (this.props.itemValues && !this.props.value) {
      this.generateControls(this.props.itemValues, this.props.defaultSelectedIds ? this.props.defaultSelectedIds.map((itm: any) => formatItemToString(itm)) : undefined);
    }

    this.handleSetContainerHeight();
  }

  componentDidUpdate(prevProps: Readonly<TwoColumnsSelectProps>) {
    if (this.props.itemValues && this.props.itemValues?.length !== this.state.allCtrls.length) {
      this.generateControls(this.props.itemValues, this.props.defaultSelectedIds ?
        this.props.defaultSelectedIds.map((itm: any) => formatItemToString(itm)) : undefined);
    }
    if (this.props.value && this.props.value?.items.length !== this.state.allCtrls.length) {
      this.generateControls(this.props.value?.items, this.props.value?.selected.map((itm: any) => {
        if (typeof itm !== 'string') {
          return itm.toString();
        }
        return itm;
      }));
    }
    if (this.props.disabledValues !== prevProps.disabledValues) {
      this.forceUpdate();
    }
  }

  componentWillUnmount() {
    this.handleUnsubscribeControls(this.state.allCtrls);
    this.handleUnsubscribeControls(this.state.selectedCtrls);
  }

  generateControls = (values: MultiColumnsSelectItemModel[], defaultSelected?: string[]) => {
    this.setState({
      selectedCtrls: [],
      allCtrls: []
    });

    const selCtrls: FormGroup[] = [];
    const allCtrls: FormGroup[] = [];

    values.forEach((v) => {
      const mappedObj = this.mapAllObjectValuesToFormGroupValues(v);
      const ctrl = FormBuilder.group({ ...mappedObj, selected: [ false ] });
      if (defaultSelected && defaultSelected.includes(v[ this.props.selectedAttributeName
        ? this.props.selectedAttributeName : 'id' ].toString())) {
        selCtrls.push(ctrl);
        ctrl.patchValue({ selected: true });
      }
      allCtrls.push(ctrl);
    });
    this.handleSubscribe(allCtrls);
    this.setState({
        allCtrls,
        selectedCtrls: selCtrls
      },
      this.handleGenerateListComponentsStyle);
    this.forceUpdate();
  };

  handleSetContainerHeight() {
    if (this.props.value?.items && this.props.value?.items.length) {
      this.allElementsListRef!.style.height = `${(this.props.value?.items.length * 28).toString()}px`;
    } else if (this.props.itemValues && this.props.itemValues.length) {
      this.allElementsListRef!.style.height = `${(this.props.itemValues?.length * 28).toString()}px`;
    } else {
      this.allElementsListRef!.style.height = '28px';
    }
  }

  mapAllObjectValuesToFormGroupValues = (obj: any) => {
    const resObj: any = {};
    Object.keys(obj).forEach((key) => {
      resObj[ key ] = [ obj[ key ] ];
    });

    return resObj;
  };

  handleSubscribe = (ctrls: FormGroup[]) => {
    ctrls.forEach((ctrl) => {
      ctrl.valueChanges.subscribe((v: any) => {
        this.setState((prevState) => {
          const prevArr = [ ...prevState.selectedCtrls ];
          if (v.selected) {
            prevArr.push(ctrl);
          } else {
            prevArr.forEach((ctrl2, inx) => {
              if (ctrl2.value.id === ctrl.value.id) {
                prevArr.splice(inx, 1);
              }
            });
          }
          this.updateValues(prevArr);
          return {
            selectedCtrls: prevArr
          };
        });
      });

    });
  };

  handleUnsubscribeControls = (ctrls: FormGroup[]) => {
    ctrls.forEach((ctrl) => {
      (ctrl.valueChanges as any).unsubscribe();
    });
  };

  handleGenerateListComponentsStyle = () => {
    const widths: number[] = [ ...this.codeAtrRefs.current.map((itm: any) => itm.clientWidth) ];
    const maxWidth = Math.max(...widths);
    const roomBetweenCodeAndItem = 12;

    // needs both setting style through dom and state as element inside FormControl doesn't detect
    // automatically external state changes
    this.codeAtrRefs.current.forEach((itm: any) => {
      itm.style.width = `${(maxWidth + roomBetweenCodeAndItem).toString()}px`;
    });

    this.handleSetContainerHeight();
    this.setState({
      codeAtrWidth: `${(maxWidth + roomBetweenCodeAndItem).toString()}px`
    });
  };

  handleSetCorrectWidthForCode = () => {
    this.codeAtrRefs.current.forEach((itm: any) => {
      if (itm) {
        itm.style.width = this.state.codeAtrWidth;
      }
    });
  };

  handleChangeFilterVal = (val: string) => {
    if (val.length) {
      this.setState({
        filter: val
      }, this.handleSetCorrectWidthForCode);
    } else {
      this.handleDeleteFilter();
    }
  };

  handleDeleteFilter = () => {
    this.setState({ filter: undefined }, this.handleSetCorrectWidthForCode);
    this.searchElRef.current!.value = '';
  };

  handleSelectAllValues = () => {
    this.setState({
      selectedCtrls: []
    });
    this.state.allCtrls.forEach((ct) => {
      if (!this.props.disabledValues?.includes(ct
        .value[ this.props.selectedAttributeName ?? 'name' ]))
        ct.patchValue({ selected: true });
    });
  };

  updateValues = (selCtrls: FormGroup[]) => {
    if (this.props.handleValueChange) {
      this.props.handleValueChange(selCtrls.map((ctrl) => ctrl.value));
    }
  };

  removeCtrl = (id: number) => {
    this.setState((prevState) => {
      const prevArr = [ ...prevState.selectedCtrls ];
      this.state.selectedCtrls.forEach((ctrl, inx) => {
        if (ctrl.value.id === id) {
          prevArr.splice(inx, 1);
        }
      });
      this.updateValues(prevArr);
      return {
        selectedCtrls: prevArr
      };
    });
    this.state.allCtrls.forEach((ctrl, inx) => {
      if (ctrl.value.id === id) {
        ctrl.patchValue({
          selected: false
        });
      }
    });
  };


  sortValuesByAtrName = (a: any, b: any): number => {
    if (this.props.sortAttributeName) {
      const atrName = this.props.sortAttributeName;
      if (a.value[ atrName ] < b.value[ atrName ]) {
        return -1;
      }
      if (a.value[ atrName ] > b.value[ atrName ]) {
        return 1;
      }
      return 0;

    }
    return 0;

  };

  sortAndFilterControls = (ctrls: FormGroup[]): FormGroup[] => ctrls.sort((a, b) => this.sortValuesByAtrName(a, b)).filter((v) => {
    const values: any = [];
    if (this.state.filter && this.props.filterAttributes) {
      this.props.filterAttributes.forEach((atr) => {
        values.push(v.value[ atr ].toLowerCase().includes(this.state.filter.toLowerCase()));
      });
    } else {
      return true;
    }
    const anyTrue = values.find((val: boolean) => val);
    return !!anyTrue;
  });

  generateSelectForControls = (ctrls: any[]) => {
    const { t } = this.props;

    const filteredCtrls = this.sortAndFilterControls(ctrls);
    if (filteredCtrls.length) {
      return filteredCtrls.map((ctrl: FormGroup) => (
        <div
          className="option-item d-flex align-items-center"
          onClick={(e) => this.handleOptionClick(e, ctrl.value.id,
            this.props.disabledValues ? this.props.disabledValues
                .includes(ctrl.value[ this.props.selectedAttributeName ? this.props.selectedAttributeName : 'name' ])
              : false)}
          key={ctrl.value.id}
          aria-hidden="true"
        >
          <FieldGroup
            strict={false}
            key={ctrl.value.id}
            control={ctrl}
            render={() => (
              <div className="pr-3 items-element-list" key={ctrl.value.id}>
                <FieldControl
                  name="selected"
                  render={(props) => (
                    <Checkbox
                      {...props}
                      id={ctrl.value.id}
                      disabled={this.props.disabledValues ? this.props.disabledValues
                        .includes(ctrl.value[ this.props.selectedAttributeName ? this.props.selectedAttributeName : 'name' ]) : false}
                    >
                      <div className="d-flex flex-row">
                        {this.props.primaryAtr
                          && (
                            <div
                              id="code-atr"
                              ref={(el) => {
                                this.codeAtrRefs.current.push(el);
                              }}
                              className="code-atrb"
                            >
                              {ctrl.value[ this.props.primaryAtr ]}
                            </div>
                          )}
                        <div className="clickable">
                          {this.props.secondaryAtr
                            ? ctrl.value[ this.props.secondaryAtr ]
                            : ctrl.value.name}
                        </div>
                      </div>
                    </Checkbox>
                  )}
                />
              </div>
            )}
          />
        </div>
      ));
    }

    return <div className="text-secondary pl-2 pt-1">{t('components.two_column_select.no_results')}</div>;

  };

  handleOptionClick = (e: any, id: any, disabled: boolean) => {
    if (disabled) {
      return;
    }
    e.preventDefault();
    this.state.allCtrls.map((itm) => {
      if (itm.value.id === id) {
        itm.patchValue({
          selected: !itm.value.selected
        });
      }
      return itm;
    });
  };

  generateListForSelectedControls = (ctrls: any[]) => ctrls.sort((a, b) => this.sortValuesByAtrName(a, b)).map((v) => (
    <div
      className="d-flex justify-content-between align-items-center selected-opt option-item"
      onClick={() => this.removeCtrl(v.value.id)}
      key={v.value.id}
    >
      <div className="d-flex flex-row selected-opt-content-container">
        {this.props.primaryAtr
          && (
            <div
              id="code-atr"
              className="code-atrb"
              style={{ width: this.state.codeAtrWidth }}
              ref={(el) => {
                this.codeAtrRefs.current.push(el);
              }}
            >
              {v.value[ this.props.primaryAtr ]}
            </div>
          )}
        <div className="selected-opt-secondary">
          {this.props.secondaryAtr
            ? v.value[ this.props.secondaryAtr ]
            : v.value.name}
        </div>
      </div>
      <div className="selected-opt-unsel d-flex justify-content-center">
      </div>
    </div>
  ));

  handleClearAll = () => {
    this.setState({
      selectedCtrls: []
    });
    this.state.allCtrls.forEach((ctrl, inx) => {
      ctrl.patchValue({
        selected: false
      });
    });
  };

  calculateAllOptionsSize = () => {
    return !this.props.disabledValues?.length ? this.state.allCtrls.length : (this.state.allCtrls.map((ctrl) =>
      !this.props.disabledValues?.includes(ctrl
        .value[ this.props.selectedAttributeName ? this.props.selectedAttributeName : 'name' ]))).length;
  };

  render() {
    const { t } = this.props;

    return (
      <div>
        <div className="d-flex flex-row">
          <div className="d-flex flex-column column-container">
            <div
              className="box-element search-element border d-flex flex-row align-items-center justify-content-between">
              <input
                value={undefined}
                placeholder={t('components.two_column_select.ph.filter')}
                className="search-input"
                onChange={(val) => this.handleChangeFilterVal(val.target.value)}
                ref={this.searchElRef}
              />
              {
                this.state.filter
                && (
                  <div className="text-primary clear-text pr-1" onClick={this.handleDeleteFilter} aria-hidden="false">
                    {t('components.two_column_select.clear_filter')}
                  </div>
                )
              }
              <div className="select-all-option text-primary" onClick={this.handleSelectAllValues}>
                {t('components.two_column_select.select_all', {
                  optionsCount: this.calculateAllOptionsSize()
                })}
              </div>
            </div>
            <div className="items-element border">
              <div ref={(divElement) => {
                if (divElement) {
                  this.allElementsListRef = divElement;
                }
              }}
              >
                {this.generateSelectForControls(this.state.allCtrls)}
              </div>
            </div>
          </div>
          <div className="column-container right">
            <div className="box-element border d-flex flex-row align-items-center justify-content-between">
              <div className="text-bold pl-2">
                {t('components.two_column_select.selected', {
                  countSelected: this.state.selectedCtrls.length
                })}
              </div>
              <div className="text-primary clear-text pr-2" onClick={this.handleClearAll}>
                {t('components.two_column_select.clear_all')}
              </div>
            </div>
            <div className="items-element right border">
              {this.generateListForSelectedControls(this.state.selectedCtrls)}
            </div>
          </div>
        </div>
      </div>
    );
  }
});
